import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { factSourceHref, type AccountFact } from '@/lib/account-knowledge';
import { cn } from '@/lib/utils';

export const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const SOURCE_LABELS: Record<AccountFact['source']['kind'], string> = {
  run: 'App', task_action: 'Tâche', chat: 'Chat', email: 'E-mail', hubspot_note: 'Note HubSpot',
  hubspot_call: 'Appel HubSpot', hubspot_meeting: 'Réunion HubSpot', hubspot_email: 'E-mail HubSpot',
};

/** Source · date · who, under a fact. */
export const factMeta = (fact: AccountFact, nameOf: (userId: string) => string | null): string => [
  fact.source.label ?? SOURCE_LABELS[fact.source.kind],
  fact.source.at,
  shortDate(fact.observedAt),
  fact.authorUserId ? nameOf(fact.authorUserId) : null,
].filter(Boolean).join(' · ');

/**
 * One fact: its sentence, where it comes from, and on click the exact quote
 * that carries it. « × » removes it at once (an undo follows).
 */
export const AccountFactRow = ({
  fact,
  accountSubjectId,
  nameOf,
  before,
  onRemove,
  fresh = false,
  index = 0,
  open: openByDefault = false,
}: {
  fact: AccountFact;
  accountSubjectId: string;
  nameOf: (userId: string) => string | null;
  /** The fact it replaced, still readable. */
  before?: AccountFact | null;
  onRemove: (fact: AccountFact) => void;
  /** Arrived since the last visit: enters with a short slide, staggered. */
  fresh?: boolean;
  index?: number;
  open?: boolean;
}) => {
  const [open, setOpen] = useState(openByDefault);
  const href = factSourceHref(fact, accountSubjectId);
  const external = href?.startsWith('https://');
  const style: CSSProperties | undefined = fresh ? { animationDelay: `${index * 45}ms`, animationFillMode: 'both' } : undefined;
  return (
    <li style={style} className={cn('group flex items-start gap-3 px-5 py-3 sm:px-6', fresh && 'motion-safe:animate-slideIn')}>
      {fresh && <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-domino-sun ring-1 ring-domino-ink/40" />}
      <div className="min-w-0 flex-1">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="block w-full text-left text-sm leading-5 text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
        >
          {fact.text}
        </button>
        <p className="mt-0.5 truncate text-xs leading-4 text-domino-inkSoft" title={factMeta(fact, nameOf)}>{factMeta(fact, nameOf)}</p>
        {before && <p className="mt-0.5 text-xs leading-4 text-domino-inkSoft">avant : {before.text} ({shortDate(before.observedAt)})</p>}
        {open && (
          <div className="mt-2 border-l-2 border-domino-sun pl-3 text-xs leading-5 text-domino-inkSoft">
            <p className="italic">« {fact.quote} »</p>
            {href && (external
              ? <a href={href} target="_blank" rel="noreferrer" className="text-domino-aubergine underline underline-offset-2">{fact.source.kind.startsWith('hubspot_') ? 'Ouvrir dans HubSpot' : 'Ouvrir dans Gmail'}</a>
              : <Link to={href} className="text-domino-aubergine underline underline-offset-2">Voir la source</Link>)}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(fact)}
        aria-label={`Retirer : ${fact.text}`}
        title="Retirer"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-domino-inkSoft transition-[opacity,transform,background-color] duration-150 ease-snap hover:bg-domino-muted hover:text-domino-ink focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune active:scale-[0.94] [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </li>
  );
};
